import React, {useEffect, useState, useRef} from 'react';
import {NavigationContainer} from '@react-navigation/native';
import {createStackNavigator} from '@react-navigation/stack';
import {LoginScreen} from '../screens/LoginScreen';
import {TwoFactorScreen} from '../screens/TwoFactorScreen';
import {MainNavigator} from './MainNavigator';
import {useAuthContext} from '../context/AuthContext';
import {ActivityIndicator, View, StyleSheet} from 'react-native';
import {setNavigationRef} from '../../shared/services/authErrorHandler';

export type RootStackParamList = {
  Login: undefined;
  TwoFactor: undefined;
  Main: undefined;
};

const Stack = createStackNavigator<RootStackParamList>();

export const AppNavigator: React.FC = () => {
  const DEV_MODE = false;
  const {isAuthenticated, needs2FA, loading} = useAuthContext();
  const navigationRef = useRef<any>(null);

  useEffect(() => {
    if (navigationRef.current) {
      setNavigationRef(navigationRef.current);
    }
  }, []);

  // Navegar automáticamente cuando cambia el estado de autenticación
  useEffect(() => {
    if (!loading && navigationRef.current?.isReady()) {
      if (!isAuthenticated && !needs2FA) {
        navigationRef.current?.navigate('Login');
      } else if (needs2FA) {
        navigationRef.current?.navigate('TwoFactor');
      } else if (isAuthenticated) {
        navigationRef.current?.navigate('Main');
      }
    }
  }, [isAuthenticated, needs2FA, loading]);

  const getInitialRouteName = (): 'Login' | 'TwoFactor' | 'Main' => {
    if (DEV_MODE) return 'Main';
    if (loading) return 'Login';
    if (needs2FA) return 'TwoFactor';
    if (isAuthenticated) return 'Main';
    return 'Login';
  };

  if (loading && !DEV_MODE) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
      </View>
    );
  }

  return (
    <NavigationContainer ref={navigationRef}>
      <Stack.Navigator
        initialRouteName={getInitialRouteName()}
        screenOptions={{
          headerShown: false,
          animationEnabled: false,
        }}>
        <Stack.Screen name="Login">
          {props => (
            <LoginScreen
              {...props}
              onLoginSuccess={() => {
                props.navigation.replace('TwoFactor');
              }}
            />
          )}
        </Stack.Screen>
        <Stack.Screen name="TwoFactor">
          {props => (
            <TwoFactorScreen
              {...props}
              onVerificationSuccess={() => {
                props.navigation.replace('Main');
              }}
              onBackToLogin={() => {
                props.navigation.replace('Login');
              }}
            />
          )}
        </Stack.Screen>
        <Stack.Screen name="Main" component={MainNavigator} />
      </Stack.Navigator>
    </NavigationContainer>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
});
